import Blaze from "@swiggy-private/blaze.js";
import { isEmpty } from "@swiggy-private/js-utils";
import { NREvent } from "@swiggy-private/checkout-types";
import { GlobalHeaderState, SwgyHeaderType } from "@checkout-core/utils/Headers";
import { getOriginalBusinessLine } from "@checkout-core/helpers/Storage";
import { NEWRELIC_TABLES } from "@checkout-core/constants/NewRelicTables";
import { isWebviewRefreshed } from "@checkout-core/helpers/WebviewRefresh";
import { parseHeroInlineXpConfig } from "@checkout-core/helpers/DineoutCart";

const isWindow = (): boolean => typeof window !== "undefined";
let isCallbackScheduled = false;
let events: NREvent[] = [];

const sendEvents = async (eventsToSend: NREvent[]): Promise<void> => {
    if (!isWindow()) {
        return;
    }

    if (__DEV__) {
        console.log(eventsToSend);
        return;
    }

    const endpoint = window.__NR_ENDPOINT__;
    const key = window.__NR_KEY__;

    if (!endpoint || !key) {
        return;
    }

    try {
        await fetch(endpoint ?? "", {
            method: "POST",
            mode: "cors",
            headers: {
                "X-Insert-Key": key ?? "",
                "Content-Type": "application/json",
            },
            body: JSON.stringify(eventsToSend),
        }).then((res) => res.json());
    } catch (err) {
        __DEV__ && console.error(err);
        events = [...eventsToSend, ...events];
    }
};

function processPendingEvents(): void {
    if (events.length > 0) {
        sendEvents(events);
        events = [];
    }

    isCallbackScheduled = false;
}

function schedulePendingEvents(): void {
    // Only schedule the rIC if one has not already been set.
    if (isCallbackScheduled) {
        return;
    }

    isCallbackScheduled = true;

    if (window.requestIdleCallback) {
        window.requestIdleCallback(processPendingEvents, { timeout: 1000 });
    } else {
        window.setTimeout(processPendingEvents, 100);
    }
}

export function getNRExtraFields(): { [key: string]: string | number } {
    let headers = GlobalHeaderState.getHeaders();
    let state = "";
    try {
        if (isEmpty(headers) && isWindow() && !isEmpty(window?.__headers__)) {
            headers = window?.__headers__ as SwgyHeaderType;
        }

        if (isWindow() && window?.location?.search) {
            const params = new URLSearchParams(window.location.search);
            state = params.get("state") || "";
        }
    } catch (error) {
        // In test environment, window.location might be undefined
        state = "";
        headers = {} as SwgyHeaderType;
    }

    // Parse heroinlinexpconfig header to extract dineout and scenes values
    const { isDineoutHeroApiInlineHtmlXp, isScenesHeroApiInlineHtmlXp, isStealdealHeroApiInlineHtmlXp } =
        parseHeroInlineXpConfig(headers);

    return {
        time: Date.now(),
        sid: headers.sid,
        userid: headers.userid,
        userAgent: headers["user-agent"],
        clientUserAgent: typeof window !== "undefined" ? window.navigator?.userAgent : "--",
        platform: headers.platform,
        deviceid: headers.deviceid || headers.deviceId || "--",
        versionCode: headers["version-code"],
        mcategory: headers.marketplacecategory,
        mbusiness: headers.marketplacebusinessline,
        environment: window.__ENVIRONMENT__ || "",
        xWebCheckoutFlow: headers["x-web-checkout-flow"] || "payment",
        isHTMLCached: window.__IS_HTML_CACHED__ || "false",
        showDineoutSuccessScreen: headers.showsuccessscreen,
        cartkey: headers["cartkey"] || "--",
        orderid: headers["orderid"] || "--",
        originalbusinessline: getOriginalBusinessLine() ?? "",
        xChannel: headers["x-channel"] || "--",
        xTheme: headers["x-theme"] || "--",
        isHeroApiInlineHtmlXp: window.isHeroApiInlineHtmlXp || "--",
        cartPagePrefetch: headers.cartpageprefetch ?? "--",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        prefetchBridgeUsed: (typeof window !== "undefined" && (window as any).__prefetchBridgeUsed__) || "--",
        deploymentType: window.__DEPLOYMENT_TYPE__ || "",
        xInternalUser: headers["x-internal-user"] ?? "--", // for differentiating preprod and production users
        state: state || "",
        isWebviewRefreshed: String(isWebviewRefreshed()),
        isStaticHTMLBuild: String(window.__IS_STATIC_HTML_BUILD__ || false),
        isRedirectionFlow: String(window.__IS_REDIRECTION_FLOW__ || false),
        staticWebAppDeployName: window.__STATIC_WEB_APP_NAME__ || "--",
        appURL: window?.location?.href || "--",
        appHost: window?.location?.host || "--",
        appPathName: window?.location?.pathname || "--",
        isDineoutHeroApiInlineHtmlXp: isDineoutHeroApiInlineHtmlXp,
        isScenesHeroApiInlineHtmlXp: isScenesHeroApiInlineHtmlXp,
        isStealdealHeroApiInlineHtmlXp,
    };
}

export function recordNREvent(event: NREvent, batch = true): void {
    if (!isWindow()) {
        return;
    }

    const extraFields = getNRExtraFields();
    const _event = { ...extraFields, ...event };

    if (typeof Blaze?.recordCustomEvent === "function" && !isEmpty(Blaze?.recordCustomEvent)) {
        Blaze.recordCustomEvent(_event, batch);
        return;
    }

    if (!batch) {
        sendEvents([_event]);
    } else {
        events.push(_event);
    }

    schedulePendingEvents();
}

export function recordGenericErrorNREvent(event: object): void {
    recordNREvent({ ...event, eventType: NEWRELIC_TABLES.GENERIC_ERROR_PAGE_IMPRESSION });
}
